import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Typography from 'kingpin/atoms/Typography';
import { useDataTypesFromMetricId } from '../../hooks/useDataTypesFromSeriesAndMetricListItems';
import useDateFields from '../../hooks/useDateFields';
import { buildDateFieldOptions } from '../DateInput/DateFieldPicker';
import useGetDatasetLabel from './MetricPicker/MetricPickerPopup/hooks/useGetDatasetLabel';
import Dropdown from './Dropdown';
import metricTypeCheckers from '../../types/metricTypeCheckers';
import Divider from '../Common/Card/Divider';

const ListNote = styled.div`
  max-width: 280px;
  padding: 4px 8px 0px 8px;
  white-space: normal;
`;

const renderListNote = (listNote: React.ReactNode) => () => (
  <>
    <ListNote>
      <Typography.Body type="Label" color="var(--color-grey-4)">
        {listNote}
      </Typography.Body>
    </ListNote>
    <div style={{ padding: '8px 0px' }}>
      <Divider noMargin />
    </div>
  </>
);

const MetricDateFieldPicker = ({
  metric,
  dateField,
  setDateField,
  isReadOnly,
  testId,
  listNote,
}: {
  metric: Metrics.Metric;
  dateField: string;
  setDateField:
    | React.Dispatch<React.SetStateAction<string>>
    | ((newDateField: string) => void);
  isReadOnly?: boolean;
  testId?: string;
  // Shown at the top of the open list, e.g. to explain why few date fields
  // are offered
  listNote?: React.ReactNode;
}) => {
  const getDatasetLabel = useGetDatasetLabel();
  const dataTypes = useDataTypesFromMetricId(metric.id);
  const { dateFields } = useDateFields({
    dataTypes,
    mustBeAvailableOnAllDataTypes: metricTypeCheckers.isCompoundMetric(metric),
  });
  // Exclude the system metadata field — recordTimeStamp reflects when a record
  // landed in the system, not a business date, so it is not a valid aggregation
  // date field (matches useEntityWindowDateFieldOptions.ts convention).
  const selectableDateFields = useMemo(
    () => dateFields.filter((f) => f.field !== 'recordTimeStamp'),
    [dateFields],
  );
  const [options, setOptions] = useState<DropdownOption[]>([]);
  useEffect(() => {
    const newOptions: DropdownOption[] = buildDateFieldOptions({
      dateFields: selectableDateFields,
      dataTypes,
      dateField,
      setDateField,
      getDatasetLabel,
    }).map((o) => ({
      ...o,
      isSelected: dateField === o.value,
    }));

    if (listNote) {
      if (newOptions.length === 0) {
        newOptions.push({
          label: 'No date fields available',
          isDisabled: true,
          onSelected: () => {},
        });
      }
      newOptions[0] = {
        ...newOptions[0],
        renderBefore: renderListNote(listNote),
      };
    }

    setOptions(newOptions);
  }, [
    dataTypes,
    dateField,
    selectableDateFields,
    getDatasetLabel,
    setDateField,
    listNote,
  ]);

  return (
    <>
      <Dropdown
        options={options}
        fullWidth
        isDisabled={isReadOnly}
        testId={testId}
      />
    </>
  );
};

export default MetricDateFieldPicker;
